import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import dateUtil from '../../../utils/dateUtil';
import ChangeRequestService, { ChangeRequest } from '../services/ChangeRequestService';
import WorkAreaService, { WorkArea } from '../../workareas/services/WorkAreaService';
import { HttpError } from '../../../core/services/HttpService';
import globalErrorHandler from '../../../core/services/GlobalErrorHandler';
import { personalakteStyles as ps, colors, shadows } from '../../employees/styles/personalakteStyles';
import { toast } from 'react-toastify';

export default function ChangeRequestReview(): React.ReactElement {
  const params = useParams();
  const navigate = useNavigate();
  const idParam = params.id ? Number(params.id) : NaN;

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [cr, setCr] = useState<ChangeRequest | null>(null);

  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [startTime, setStartTime] = useState<string>('');
  const [endTime, setEndTime] = useState<string>('');
  const [notes, setNotes] = useState<string>('');

  const [allWAs, setAllWAs] = useState<WorkArea[]>([]);
  const [workAreaId, setWorkAreaId] = useState<number | null>(null);
  const [showWA, setShowWA] = useState<boolean>(false);
  const [loadingWA, setLoadingWA] = useState<boolean>(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        if (!idParam || Number.isNaN(idParam)) {
          setError('Invalid change request id');
          return;
        }
        const data = await ChangeRequestService.fetchById(idParam);
        setCr(data);

        const startIso = getRequestedStart(data) || getOriginalStart(data) || '';
        const endIso = getRequestedEnd(data) || getOriginalEnd(data) || '';
        const sdt = startIso ? new Date(startIso) : null;
        const edt = endIso ? new Date(endIso) : null;
        setStartDate(sdt ? sdt.toISOString().split('T')[0] : '');
        setEndDate(edt ? edt.toISOString().split('T')[0] : '');
        setStartTime(extractTime(startIso));
        setEndTime(extractTime(endIso));
        setWorkAreaId(getRequestedWAId(data) || getOriginalWAId(data) || null);
        setNotes((data as any)?.employee_notes || '');
      } catch (err) {
        if (err instanceof HttpError) {
          if (!globalErrorHandler.handle(err)) setError('Failed to load change request');
        } else {
          setError('Failed to load change request');
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [idParam]);

  const fmtLocalFancy = (iso?: string | null): string => {
    if (!iso) return '';
    const dt = dateUtil.convertUTCToTenantTime(iso);
    if (!dt) return '';
    const wd = dt.toLocaleDateString('en-GB', { weekday: 'short' });
    const dd = String(dt.getDate()).padStart(2, '0');
    const mm = String(dt.getMonth() + 1).padStart(2, '0');
    const yy = String(dt.getFullYear()).slice(-2);
    const hh = String(dt.getHours()).padStart(2, '0');
    const mi = String(dt.getMinutes()).padStart(2, '0');
    return `${wd}, ${dd}.${mm}.${yy}, ${hh}:${mi} h`;
  };

  const pickFirst = (obj: any, keys: string[]): any => {
    if (!obj) return undefined;
    for (const k of keys) {
      if (obj[k] !== undefined && obj[k] !== null && obj[k] !== '') return obj[k];
    }
    return undefined;
  };

  const getOriginalStart = (cr?: any) => pickFirst(cr || {}, ['original_start_time', 'original_start_datetime', 'original_start']);
  const getOriginalEnd   = (cr?: any) => pickFirst(cr || {}, ['original_end_time', 'original_end_datetime', 'original_end']);
  const getRequestedStart= (cr?: any) => pickFirst(cr || {}, ['requested_start_time', 'proposed_start_time', 'requested_start_datetime', 'requested_start']);
  const getRequestedEnd  = (cr?: any) => pickFirst(cr || {}, ['requested_end_time', 'proposed_end_time', 'requested_end_datetime', 'requested_end']);
  const getOriginalWAId  = (cr?: any) => pickFirst(cr || {}, ['original_work_area', 'original_work_area_id']);
  const getRequestedWAId = (cr?: any) => pickFirst(cr || {}, ['requested_work_area', 'proposed_work_area']);

  const buildISOFromFields = (dateStr?: string, timeStr?: string): string | null => {
    if (!dateStr || !timeStr) return null;
    return dateUtil.createTenantTimeAsUTC(dateStr, timeStr);
  };

  const extractTime = (iso?: string | null): string => {
    if (!iso) return '';
    return dateUtil.extractTimeFromISO(iso);
  };

  const diffText = useMemo(() => {
    if (!cr) return '';
    const startIsoNew = buildISOFromFields(startDate, startTime);
    const endIsoNew = buildISOFromFields(endDate, endTime);
    if (!startIsoNew || !endIsoNew) return '';
    const s = new Date(startIsoNew);
    const e = new Date(endIsoNew);
    const overnight = e < s;
    return `${fmtLocalFancy(getRequestedStart(cr) || getOriginalStart(cr))} → ${fmtLocalFancy(startIsoNew)} | ${fmtLocalFancy(getRequestedEnd(cr) || getOriginalEnd(cr))} → ${fmtLocalFancy(endIsoNew)}${overnight ? ' (overnight)' : ''}`;
  }, [cr, startDate, startTime, endDate, endTime]);

  const toggleWA = async () => {
    setShowWA(prev => !prev);
    if (!showWA) {
      setLoadingWA(true);
      try {
        const all = await WorkAreaService.fetchWorkAreas();
        setAllWAs(all);
      } catch (err) {
        if (err instanceof HttpError) globalErrorHandler.handle(err);
      } finally {
        setLoadingWA(false);
      }
    }
  };

  const handleSubmit = async () => {
    if (!cr) return;
    const startIso = buildISOFromFields(startDate, startTime);
    const endIso = buildISOFromFields(endDate, endTime);
    let waNote: string | null = null;
    if (workAreaId && getOriginalWAId(cr) && workAreaId !== getOriginalWAId(cr)) {
      const waObj = allWAs.find(w => w.id === workAreaId);
      waNote = waObj ? `WA requested: ${waObj.name} (${waObj.id})` : `WA requested: (${workAreaId})`;
    }
    const finalNotes = [notes || '', waNote || ''].filter(Boolean).join('\n');
    try {
      await ChangeRequestService.submitEmployeeReview(cr.id, {
        new_start_time: startIso,
        new_end_time: endIso,
        employee_notes: finalNotes || undefined
      });
      navigate(-1);
    } catch (err) {
      if (err instanceof HttpError) {
        if (!globalErrorHandler.handle(err)) toast.error('Failed to submit');
      } else {
        toast.error('Failed to submit');
      }
    }
  };

  return (
    <div style={{ padding: '16px' }}>
      <h2 style={{ marginTop: 0 }}>Edit Change Request</h2>
      {loading && <div>Loading...</div>}
      {error && <div style={{ color: colors.danger }}>{error}</div>}
      {!loading && cr && (
        <div style={{ maxWidth: 640 }}>
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>Original</div>
            <div>
              {fmtLocalFancy(getOriginalStart(cr))} – {fmtLocalFancy(getOriginalEnd(cr))}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
            <div>
              <label style={{ ...ps.labelStyle, marginBottom: 4 }}>Start Date</label>
              <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} style={{ width: '100%', padding: 6 }} />
            </div>
            <div>
              <label style={{ ...ps.labelStyle, marginBottom: 4 }}>End Date</label>
              <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} style={{ width: '100%', padding: 6 }} />
            </div>
            <div>
              <label style={{ ...ps.labelStyle, marginBottom: 4 }}>Start Time (HH:MM)</label>
              <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} style={{ width: '100%', padding: 6 }} />
            </div>
            <div>
              <label style={{ ...ps.labelStyle, marginBottom: 4 }}>End Time (HH:MM)</label>
              <input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} style={{ width: '100%', padding: 6 }} />
            </div>
          </div>

          <div style={{ marginBottom: 10 }}>
            <label style={{ ...ps.labelStyle, marginBottom: 4 }}>Notes</label>
            <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} style={{ width: '100%', padding: 6 }} />
          </div>

          <div style={{ marginBottom: 10 }}>
            <button style={{ backgroundColor: colors.bgMuted, color: colors.navy, border: 'none', borderRadius: 6, padding: '6px 10px', cursor: 'pointer' }} onClick={toggleWA}>
              {showWA ? 'Hide Work Area' : 'Change Work Area'}
            </button>
            {showWA && (
              <div style={{ marginTop: 8 }}>
                {loadingWA ? (
                  <div>Loading work areas...</div>
                ) : (
                  <select value={workAreaId ?? ''} onChange={(e) => setWorkAreaId(e.target.value ? Number(e.target.value) : null)} style={{ padding: 6, width: '100%' }}>
                    <option value="">(none)</option>
                    {allWAs.map(wa => (
                      <option key={wa.id} value={wa.id}>{wa.name}</option>
                    ))}
                  </select>
                )}
              </div>
            )}
          </div>

          {diffText && (
            <div style={{ background: colors.bgMuted, border: `1px solid ${colors.borderLight}`, borderRadius: 6, padding: 10, marginBottom: 12 }}>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>Diff</div>
              <div style={{ color: colors.textBody }}>{diffText}</div>
            </div>
          )}

          <div style={{ textAlign: 'right' }}>
            <button onClick={handleSubmit} style={{ ...ps.buttonPrimary, padding: '6px 12px' }}>Submit</button>
            <button onClick={() => navigate(-1)} style={{ ...ps.buttonSecondary, padding: '6px 12px', marginLeft: 8 }}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
} 